import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Bell, X, Clock, AlertTriangle, Search, Users, CheckCircle, Loader2, Save
} from 'lucide-react';

const ROLE_LABELS = {
  admin: 'Administrator',
  // Internal: Management
  director: 'Director', project_director: 'Project Director', project_manager: 'Project Manager',
  // Internal: Commercial / Planning
  contracts_manager: 'Contracts Manager', commercial_manager: 'Commercial Manager',
  quantity_surveyor: 'Quantity Surveyor', planner: 'Planner',
  // Internal: Site Delivery
  construction_manager: 'Construction Manager', site_agent: 'Site Agent',
  site_manager: 'Site Manager', site_engineer: 'Site Engineer', foreman: 'Foreman',
  // Internal: Support
  procurement_manager: 'Procurement Manager', plant_manager: 'Plant Manager',
  stores_manager: 'Stores Manager', document_controller: 'Document Controller',
  quality_manager: 'Quality Manager', safety_officer: 'Safety Officer',
  project_administrator: 'Project Administrator', other: 'Other',
  // Professional Team
  prof_principal_agent: 'Principal Agent', prof_project_manager: 'Project Manager (Prof)',
  prof_architect: 'Architect', prof_architectural_technologist: 'Architectural Technologist',
  prof_quantity_surveyor: 'QS (Professional)', prof_civil_engineer: 'Civil Engineer',
  prof_structural_engineer: 'Structural Engineer', prof_electrical_engineer: 'Electrical Engineer',
  prof_mechanical_engineer: 'Mechanical Engineer', prof_wet_services_engineer: 'Wet Services Engineer',
  prof_fire_engineer: 'Fire Engineer', prof_electronic_ict_engineer: 'Electronic / ICT Engineer',
  prof_interior_designer: 'Interior Designer', prof_landscape_architect: 'Landscape Architect',
  prof_town_planner: 'Town Planner', prof_traffic_engineer: 'Traffic Engineer',
  prof_geotechnical_engineer: 'Geotechnical Engineer',
  prof_environmental_consultant: 'Environmental Consultant',
  prof_health_safety_consultant: 'H&S Consultant', prof_clerk_of_works: 'Clerk of Works',
  prof_other: 'Other (Professional)',
  // Legacy aliases
  qs: 'Quantity Surveyor', cm: 'Contracts Manager', contracts_director: 'Director',
  professional_team: 'Principal Agent', consultant: 'Principal Agent',
  hs_officer: 'Safety Officer', project_admin: 'Project Administrator',
  contractor: 'Contracts Manager',
};

export default function NotificationSettingsModal({
  notifSettings, setNotifSettings, notifSaving, saveNotifSettings,
  teamMembers, notifLog,
  recipientSearch, setRecipientSearch,
  activeRoleFilter, setActiveRoleFilter,
  onClose,
}) {
  const availableRoles = [...new Set(teamMembers.map(m => m.role).filter(Boolean))].sort();

  const filteredTeamMembers = teamMembers.filter(m => {
    const matchesSearch = !recipientSearch ||
      (m.name || '').toLowerCase().includes(recipientSearch.toLowerCase()) ||
      (m.email || '').toLowerCase().includes(recipientSearch.toLowerCase());
    const matchesRole = !activeRoleFilter || m.role === activeRoleFilter;
    return matchesSearch && matchesRole;
  });

  const toggleRecipient = (userId) => {
    setNotifSettings(prev => {
      const ids = prev.recipient_user_ids || [];
      return {
        ...prev,
        recipient_user_ids: ids.includes(userId) ? ids.filter(id => id !== userId) : [...ids, userId],
      };
    });
  };

  const selectAll = () => {
    const allIds = filteredTeamMembers.map(m => m.id);
    setNotifSettings(prev => ({
      ...prev,
      recipient_user_ids: [...new Set([...(prev.recipient_user_ids || []), ...allIds])],
    }));
  };

  const deselectAll = () => {
    const filteredIds = new Set(filteredTeamMembers.map(m => m.id));
    setNotifSettings(prev => ({
      ...prev,
      recipient_user_ids: (prev.recipient_user_ids || []).filter(id => !filteredIds.has(id)),
    }));
  };

  const selectByRole = (role) => {
    const roleIds = teamMembers.filter(m => m.role === role).map(m => m.id);
    setNotifSettings(prev => ({
      ...prev,
      recipient_user_ids: [...new Set([...(prev.recipient_user_ids || []), ...roleIds])],
    }));
  };

  const allFilteredSelected = filteredTeamMembers.length > 0 &&
    filteredTeamMembers.every(m => (notifSettings.recipient_user_ids || []).includes(m.id));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="diary-notif-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-cyan-600" />
            <h2 className="text-lg font-bold text-slate-900">Daily Diary Notifications</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
            <div>
              <p className="font-semibold text-slate-800 text-sm">Enable Diary Reminders</p>
              <p className="text-xs text-slate-500 mt-0.5">Send automatic notifications at 15:00 and 16:30 SAST</p>
            </div>
            <button
              onClick={() => setNotifSettings(p => ({ ...p, enabled: !p.enabled }))}
              className={`w-11 h-6 rounded-full transition-colors flex items-center ${notifSettings.enabled ? 'bg-cyan-600 justify-end' : 'bg-slate-300 justify-start'}`}
              data-testid="diary-notif-toggle"
            >
              <div className="w-5 h-5 bg-white rounded-full shadow-md mx-0.5" />
            </button>
          </div>

          {notifSettings.enabled && (
            <>
              {/* Skip Options */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Skip Notifications On</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={notifSettings.skip_weekends} onChange={e => setNotifSettings(p => ({ ...p, skip_weekends: e.target.checked }))} className="rounded border-slate-300" data-testid="diary-notif-skip-weekends" />
                  Weekends (Saturday & Sunday)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={notifSettings.skip_public_holidays} onChange={e => setNotifSettings(p => ({ ...p, skip_public_holidays: e.target.checked }))} className="rounded border-slate-300" data-testid="diary-notif-skip-holidays" />
                  South African Public Holidays
                </label>
              </div>

              {/* Schedule Info */}
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-cyan-800 mb-1">Notification Schedule (SAST)</p>
                <div className="flex gap-4 text-xs text-cyan-700">
                  <div className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> <span className="font-medium">15:00</span> — Start reminder</div>
                  <div className="flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> <span className="font-medium">16:30</span> — Final reminder</div>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">
                    Recipients <span className="text-slate-400 font-normal">({(notifSettings.recipient_user_ids || []).length} selected)</span>
                  </p>
                  <button onClick={allFilteredSelected ? deselectAll : selectAll} className="text-xs font-medium text-cyan-600 hover:text-cyan-800 transition-colors" data-testid="diary-notif-select-all-btn">
                    {allFilteredSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input value={recipientSearch} onChange={e => setRecipientSearch(e.target.value)} placeholder="Search by name or email..." className="pl-8 h-8 text-xs" data-testid="diary-notif-search" />
                </div>

                {/* Role Filter Pills */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <button onClick={() => setActiveRoleFilter(null)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${!activeRoleFilter ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="diary-notif-role-all">
                    All Roles
                  </button>
                  {availableRoles.map(role => {
                    const count = teamMembers.filter(m => m.role === role).length;
                    const selectedInRole = teamMembers.filter(m => m.role === role && (notifSettings.recipient_user_ids || []).includes(m.id)).length;
                    return (
                      <button key={role} onClick={() => setActiveRoleFilter(activeRoleFilter === role ? null : role)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors flex items-center gap-1 ${activeRoleFilter === role ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`diary-notif-role-${role}`}>
                        {ROLE_LABELS[role] || role}
                        <span className={`${activeRoleFilter === role ? 'text-cyan-200' : 'text-slate-400'}`}>{selectedInRole}/{count}</span>
                      </button>
                    );
                  })}
                </div>

                {activeRoleFilter && (
                  <button onClick={() => selectByRole(activeRoleFilter)} className="w-full mb-2 py-1.5 rounded-lg border border-dashed border-cyan-300 text-xs font-medium text-cyan-700 hover:bg-cyan-50 transition-colors flex items-center justify-center gap-1" data-testid="diary-notif-select-role-btn">
                    <Users className="h-3.5 w-3.5" />
                    Select all {ROLE_LABELS[activeRoleFilter] || activeRoleFilter}s ({teamMembers.filter(m => m.role === activeRoleFilter).length})
                  </button>
                )}

                <div className="border border-slate-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-slate-100">
                  {teamMembers.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" /> Loading team...</div>
                  ) : filteredTeamMembers.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-400">No users match your search.</div>
                  ) : filteredTeamMembers.map(m => {
                    const selected = (notifSettings.recipient_user_ids || []).includes(m.id);
                    return (
                      <label key={m.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors ${selected ? 'bg-cyan-50/50' : ''}`} data-testid={`diary-notif-user-${m.id}`}>
                        <input type="checkbox" checked={selected} onChange={() => toggleRecipient(m.id)} className="rounded border-slate-300" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{m.name || m.email}</p>
                          <p className="text-xs text-slate-400 truncate">{m.email} {m.role ? `· ${ROLE_LABELS[m.role] || m.role}` : ''}</p>
                        </div>
                        {selected && <CheckCircle className="h-4 w-4 text-cyan-600 flex-shrink-0" />}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Recent Notification Log */}
              {notifLog.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Recent Notifications Sent</p>
                  <div className="space-y-1.5">
                    {notifLog.slice(0, 5).map(log => (
                      <div key={log.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Bell className="h-3 w-3 text-slate-400" />
                          <span className="text-slate-600">{log.date}</span>
                          <Badge className={log.reminder_type === 'final' ? 'bg-red-100 text-red-700 text-[10px]' : 'bg-blue-100 text-blue-700 text-[10px]'}>
                            {log.reminder_type === 'final' ? '16:30' : '15:00'}
                          </Badge>
                        </div>
                        <span className="text-slate-400">{log.recipients_count} recipients</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-xl">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={saveNotifSettings} className="bg-cyan-600 hover:bg-cyan-700" disabled={notifSaving} data-testid="diary-notif-save-btn">
            {notifSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
}
